import type { EntityType, QueryFilter } from '@mariana/labos-client';
import type { LabSelection } from '../LabContext';

export interface ColumnDef {
  /**
   * Attribute key. Use dot-notation (`fkAttr.targetAttr`) for cross-entity
   * columns (e.g. `sample_id.sample_name`). The FK attribute's
   * `referenced_entity_type` is resolved at runtime from the attribute cache.
   */
  key: string;
  label: string;
  /**
   * 'text' renders an extract column as-is, bypassing the analytical number formatter.
   * 'number' formats a scalar numeric attribute through formatAnalyticalValue (same
   * tidy formatting the element/RSD extract columns get), instead of raw String(value).
   */
  render?: 'date' | 'link' | 'state' | 'json' | 'icpMeasurementDates' | 'text' | 'number';
  /** Pull a nested value from a JSON attribute (e.g. worksheet_data). */
  extract?: { from: string; path: string };
  /** Whether this column supports server-side sorting. Defaults to true; false for extract columns. */
  sortable?: boolean;
}

export interface ViewDefinition {
  id: string;
  label: string;
  description?: string;
  entityType: EntityType;
  filters: QueryFilter[];
  columns: ColumnDef[];
  /**
   * Curated columns offered in the column picker but not shown by default.
   * Included in the "known columns" universe so they persist / resolve from
   * shared URLs, but excluded from the default-visible set.
   */
  optionalColumns?: ColumnDef[];
  /**
   * Per-lab overrides of the default-visible `columns`, keyed by lab id. When the
   * active lab has an entry, it replaces `columns` as the default set (optional
   * columns still apply). Mirrors ENTITY_COLUMNS_BY_LAB. Resolve via getViewColumns.
   */
  columnsByLab?: Partial<Record<LabSelection, ColumnDef[]>>;
  resultViewer?: string;
  defaultSort?: { by: string; order: 'asc' | 'desc' };
  groupByKey?: string;
}

const ICP_ELEMENTS = [
  'Al', 'As', 'B', 'Ba', 'Be', 'Br', 'Ca', 'Cd', 'Cl', 'Co',
  'Cr', 'Cu', 'Fe', 'I', 'K', 'Li', 'Mg', 'Mn', 'Na', 'Ni',
  'P', 'Pb', 'S', 'Sb', 'Si', 'Sn', 'Sr', 'Ti', 'Zn',
] as const;

function icpElementColumns(): ColumnDef[] {
  return ICP_ELEMENTS.map(el => ({
    key: `icp_${el.toLowerCase()}`,
    label: el,
    extract: { from: 'worksheet_data', path: `report_export_${el.toLowerCase()}.value` },
    sortable: false,
  }));
}

function icpElementRsdColumns(): ColumnDef[] {
  return ICP_ELEMENTS.map(el => ({
    key: `icp_${el.toLowerCase()}_rsd`,
    label: `${el} RSD (%)`,
    extract: { from: 'worksheet_data', path: `report_export_${el.toLowerCase()}.rsd` },
    sortable: false,
  }));
}

const TEST_BASE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'Test ID' },
  { key: 'sample_id', label: 'Sample', render: 'link' },
  { key: 'sample_id.sample_name', label: 'Sample Name' },
  { key: 'sample_id.sample_point', label: 'Sample Point' },
  { key: 'sample_id.order_id', label: 'Order' },
  { key: 'sample_id.mm_los_experiment_name', label: 'Experiment' },
  { key: 'sample_id.mm_los_experiment_campaign_name', label: 'Campaign' },
  { key: 'assay_id.title', label: 'Assay' },
  { key: 'state', label: 'State', render: 'state' },
  { key: 'sample_id.collection_timestamp', label: 'Collected', render: 'date' },
  { key: 'date_created', label: 'Created', render: 'date' },
  { key: 'complete_date', label: 'Completed', render: 'date' },
];

export const VIEW_REGISTRY: Record<string, ViewDefinition> = {
  icp: {
    id: 'icp',
    label: 'ICP',
    description: 'ARCOS ICP elemental analysis tests',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [4, 15, 16, 24, 25, 28, 29, 31, 42, 43, 44, 45, 47, 51, 52, 56, 61] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      // Sortable representative timestamp (latest measurementDate across dilutions),
      // promoted to a scalar attribute by the ICP sync.
      { key: 'icp_measurement_date', label: 'Measured', render: 'date' },
      // Full per-dilution measurement dates, labeled by dilution factor. Display-only.
      { key: 'icp_xml_data', label: 'Measurement Runs', render: 'icpMeasurementDates', sortable: false },
      ...icpElementColumns(),
    ],
    // Copper One (lab 2): a focused default that surfaces just Cu/Fe with the DMC
    // correction factor and recovered RSD — the columns Cu1 operators actually use
    // — instead of the full 29-element grid. Other elements' values, RSD / Cu
    // Fraction / DMC stay available via the column picker (optionalColumns).
    columnsByLab: {
      2: [
        { key: 'id', label: 'Test ID' },
        { key: 'sample_id', label: 'Sample', render: 'link' },
        { key: 'sample_id.sample_name', label: 'Sample Name' },
        { key: 'sample_id.sample_description', label: 'Sample Description' },
        { key: 'assay_id.title', label: 'Assay' },
        { key: 'state', label: 'State', render: 'state' },
        { key: 'sample_id.collection_datestamp', label: 'Collected' },
        { key: 'icp_measurement_date', label: 'Measured', render: 'date' },
        { key: 'icp_xml_data', label: 'Measurement Runs', render: 'icpMeasurementDates', sortable: false },
        { key: 'icp_cu', label: 'Cu', extract: { from: 'worksheet_data', path: 'report_export_cu.value' }, sortable: false },
        { key: 'icp_fe', label: 'Fe', extract: { from: 'worksheet_data', path: 'report_export_fe.value' }, sortable: false },
        { key: 'icp_dmc_correction_factor', label: 'DMC Correction Factor', render: 'number' },
        { key: 'icp_cu_rsd', label: 'Cu RSD (%)', extract: { from: 'worksheet_data', path: 'report_export_cu.rsd' }, sortable: false },
        { key: 'icp_fe_rsd', label: 'Fe RSD (%)', extract: { from: 'worksheet_data', path: 'report_export_fe.rsd' }, sortable: false },
      ],
    },
    optionalColumns: [
      {
        key: 'icp_cu_fraction',
        label: 'Cu Fraction',
        extract: { from: 'worksheet_data', path: 'report_export_cu_fraction.value' },
        sortable: false,
      },
      // DMC (dynamical measurement corrected) correction factor for Cu, promoted
      // to a scalar attribute by the Cu1 sync so it is header-sortable and
      // filterable. Only populated for Copper One tests that use the DMC step.
      // render:'number' keeps the raw ratio in the DB (exact filter/sort) but
      // formats the cell like the adjacent element/RSD columns.
      { key: 'icp_dmc_correction_factor', label: 'DMC Correction Factor', render: 'number' },
      // Element values (Al, Mn, …) so Cu1 can add them with short labels after
      // the focused default hid them. ColumnPicker skips keys already in the
      // lab's default set (HQ's full grid, Cu1's Cu/Fe).
      ...icpElementColumns(),
      // Per-element RSD (%) — relative standard deviation of the replicate
      // measurements, written by the ICP sync alongside value/unit/stdDev.
      ...icpElementRsdColumns(),
    ],
    resultViewer: 'icp',
    defaultSort: { by: 'id', order: 'desc' },
  },
  nmr: {
    id: 'nmr',
    label: 'NMR',
    description: 'NanoNord NMR tests',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [10, 23, 30] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      { key: 'nmr_li', label: 'Li', extract: { from: 'worksheet_data', path: 'Li_result' }, sortable: false },
      { key: 'nmr_na', label: 'Na', extract: { from: 'worksheet_data', path: 'Na_result' }, sortable: false },
      { key: 'nmr_cl', label: 'Cl', extract: { from: 'worksheet_data', path: 'Cl' }, sortable: false },
      { key: 'nmr_b', label: 'B', extract: { from: 'worksheet_data', path: 'B_result' }, sortable: false },
      { key: 'nmr_br', label: 'Br', extract: { from: 'worksheet_data', path: 'Br_result' }, sortable: false },
    ],
    resultViewer: 'nmr',
    defaultSort: { by: 'id', order: 'desc' },
  },
  shimadzu: {
    id: 'shimadzu',
    label: 'Shimadzu',
    description: 'Shimadzu TOC/TN carbon & nitrogen analysis tests',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [11, 12, 14, 19, 21, 22] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      ...['TOC', 'TC', 'IC', 'POC', 'NPOC', 'TN'].map(param => ({
        key: `shimadzu_${param.toLowerCase()}`,
        label: param,
        extract: { from: 'worksheet_data', path: `report_export_result_${param.toLowerCase()}_.value` },
        sortable: false,
      })),
    ],
    resultViewer: 'shimadzu',
    defaultSort: { by: 'id', order: 'desc' },
  },
  oilInWater: {
    id: 'oilInWater',
    label: 'Oil in Water',
    description: 'Oil in Water tests — TD-560 two-aliquot results in mg/L, plus legacy BKG(WSO)/OIW reports in ppm',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [46] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      // Both report formats write the result under report_export_oiw, so this
      // column spans TD-560 (mg/L) and legacy (ppm) tests alike.
      { key: 'oiw_oiw', label: 'OIW', extract: { from: 'worksheet_data', path: 'report_export_oiw.value' }, sortable: false },
      { key: 'oiw_unit', label: 'Unit', render: 'text', extract: { from: 'worksheet_data', path: 'report_export_unit.value' }, sortable: false },
      { key: 'oiw_method', label: 'Method', render: 'text', extract: { from: 'worksheet_data', path: 'report_export_method.value' }, sortable: false },
    ],
    // Per-aliquot averages (TD-560) and legacy WSO/OIW-only parameters —
    // available in the column picker, hidden by default.
    optionalColumns: [
      { key: 'oiw_td560_aliquot1_avg', label: 'Aliquot 1 Avg', extract: { from: 'worksheet_data', path: 'report_export_aliquot1_average.value' }, sortable: false },
      { key: 'oiw_td560_aliquot2_avg', label: 'Aliquot 2 Avg', extract: { from: 'worksheet_data', path: 'report_export_aliquot2_average.value' }, sortable: false },
      { key: 'oiw_bkg_wso', label: 'BKG(WSO)', extract: { from: 'worksheet_data', path: 'report_export_bkg_wso.value' }, sortable: false },
      { key: 'oiw_dispersed_oil', label: 'Dispersed Oil', extract: { from: 'worksheet_data', path: 'report_export_dispersed_oil.value' }, sortable: false },
    ],
    resultViewer: 'oilInWater',
    defaultSort: { by: 'id', order: 'desc' },
  },
  titrator: {
    id: 'titrator',
    label: 'Titration',
    description: 'Auto-titrator tests (carbonate & bicarbonate in solution, lithium carbonate purity)',
    entityType: 'test',
    // Prod assay ids (58 = solution, 59 = Li2CO3 purity), created via the
    // create-titrator-assays CLI (POS-1406/1407/1408).
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [58, 59] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      { key: 'titrator_method', label: 'Method', extract: { from: 'worksheet_data', path: 'report_export_method.value' }, sortable: false },
      { key: 'titrator_li2co3_purity', label: 'Li2CO3 Purity (%)', extract: { from: 'worksheet_data', path: 'report_export_li2co3_purity.value' }, sortable: false },
      { key: 'titrator_bicarbonate', label: 'Bicarbonate', extract: { from: 'worksheet_data', path: 'report_export_bicarbonate.value' }, sortable: false },
      { key: 'titrator_carbonate', label: 'Carbonate', extract: { from: 'worksheet_data', path: 'report_export_carbonate.value' }, sortable: false },
      { key: 'titrator_bicarbonate_unit', label: 'Bicarb. Unit', extract: { from: 'worksheet_data', path: 'report_export_bicarbonate.unit' }, sortable: false },
    ],
    optionalColumns: [
      { key: 'titrator_replicates', label: 'Replicates', extract: { from: 'worksheet_data', path: 'report_export_replicates.value' }, sortable: false },
      { key: 'titrator_bicarbonate_srel', label: 'Bicarb. srel (%)', extract: { from: 'worksheet_data', path: 'report_export_stat_bicarbonate_in_solution_srel.value' }, sortable: false },
      { key: 'titrator_carbonate_srel', label: 'Carb. srel (%)', extract: { from: 'worksheet_data', path: 'report_export_stat_carbonate_in_solution_srel.value' }, sortable: false },
    ],
    resultViewer: 'titrator',
    defaultSort: { by: 'id', order: 'desc' },
  },
  loi: {
    id: 'loi',
    label: 'Loss on Ignition',
    description: 'Loss on Ignition (LOI) tests reporting % mass loss after ignition',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [57] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      { key: 'loi_percent', label: 'LOI (%)', extract: { from: 'worksheet_data', path: 'report_export_loi.value' }, sortable: false },
      { key: 'loi_temperature', label: 'Temperature (°C)', extract: { from: 'worksheet_data', path: 'report_export_temperature.value' }, sortable: false },
      { key: 'loi_analysis_time', label: 'Analysis Time', render: 'text', extract: { from: 'worksheet_data', path: 'report_export_analysis_time.value' }, sortable: false },
    ],
    resultViewer: 'loi',
    defaultSort: { by: 'id', order: 'desc' },
  },
  camsizer: {
    id: 'camsizer',
    label: 'Particle Size',
    description: 'Camsizer particle-size distribution tests (lithium carbonate, copper wire, copper powder)',
    entityType: 'test',
    filters: [{ attribute_name: 'assay_id', operator: 'IN', value: [53, 54, 55] }],
    columns: [
      ...TEST_BASE_COLUMNS,
      { key: 'camsizer_d10', label: 'D10 (µm)', extract: { from: 'worksheet_data', path: 'report_export_x_um_at_q3_10_0.value' }, sortable: false },
      { key: 'camsizer_d50', label: 'D50 (µm)', extract: { from: 'worksheet_data', path: 'report_export_x_um_at_q3_50_0.value' }, sortable: false },
      { key: 'camsizer_d90', label: 'D90 (µm)', extract: { from: 'worksheet_data', path: 'report_export_x_um_at_q3_90_0.value' }, sortable: false },
    ],
    optionalColumns: [
      { key: 'camsizer_mean_spht3', label: 'Mean SPHT3', extract: { from: 'worksheet_data', path: 'report_export_mean_value_spht3.value' }, sortable: false },
      { key: 'camsizer_mean_symm3', label: 'Mean Symm3', extract: { from: 'worksheet_data', path: 'report_export_mean_value_symm3.value' }, sortable: false },
      { key: 'camsizer_mean_bl3', label: 'Mean b/l3', extract: { from: 'worksheet_data', path: 'report_export_mean_value_b_l3.value' }, sortable: false },
      { key: 'camsizer_mean_c3', label: 'Mean C3', extract: { from: 'worksheet_data', path: 'report_export_mean_value_c3.value' }, sortable: false },
    ],
    resultViewer: 'camsizer',
    defaultSort: { by: 'id', order: 'desc' },
  },
  orders: {
    id: 'orders',
    label: 'Orders (with Samples)',
    description: 'Samples organized by order, most recent orders first',
    entityType: 'sample',
    filters: [],
    columns: [
      { key: 'order_id', label: 'Order', render: 'link' },
      { key: 'id', label: 'Sample ID' },
      { key: 'sample_name', label: 'Name' },
      { key: 'sample_point', label: 'Sample Point' },
      { key: 'collection_site', label: 'Site' },
      { key: 'sample_matrix', label: 'Matrix' },
      { key: 'complete', label: 'Complete' },
      { key: 'date_received', label: 'Received', render: 'date' },
      { key: 'test_count', label: 'Tests' },
    ],
    defaultSort: { by: 'order_id', order: 'desc' },
    groupByKey: 'order_id',
  },
};

export const ENTITY_COLUMNS: Record<EntityType, ColumnDef[]> = {
  sample: [
    { key: 'id', label: 'ID' },
    { key: 'sample_name', label: 'Name' },
    { key: 'sample_point', label: 'Sample Point' },
    { key: 'collection_site', label: 'Site' },
    { key: 'sample_matrix', label: 'Matrix' },
    { key: 'order_id', label: 'Order', render: 'link' },
    { key: 'complete', label: 'Complete' },
    { key: 'date_received', label: 'Received', render: 'date' },
    { key: 'test_count', label: 'Tests' },
  ],
  test: [
    { key: 'id', label: 'ID' },
    { key: 'sample_id', label: 'Sample', render: 'link' },
    { key: 'sample_id.sample_name', label: 'Sample Name' },
    { key: 'assay_id.title', label: 'Assay' },
    { key: 'state', label: 'State', render: 'state' },
    { key: 'start_date', label: 'Started', render: 'date' },
    { key: 'complete_date', label: 'Completed', render: 'date' },
  ],
  assay: [
    { key: 'id', label: 'ID' },
    { key: 'title', label: 'Title' },
    { key: 'active', label: 'Active' },
  ],
  order: [
    { key: 'id', label: 'ID' },
    { key: 'submitted_by', label: 'Submitted By' },
    { key: 'lab_id.name', label: 'Lab' },
    { key: 'state', label: 'State', render: 'state' },
    { key: 'date_created', label: 'Created', render: 'date' },
    { key: 'date_received', label: 'Received', render: 'date' },
  ],
  attachment: [
    { key: 'id', label: 'ID' },
    { key: 'filename', label: 'Filename' },
    { key: 'content_type', label: 'Type' },
    { key: 'parent_entity_type', label: 'Parent Type' },
    { key: 'parent_entity_id', label: 'Parent ID' },
    { key: 'file_size', label: 'Size' },
  ],
  lab: [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'timezone', label: 'Timezone' },
  ],
};

/**
 * Per-lab column overrides. Only entity types that differ between labs need entries.
 * Any entity type not listed here falls back to ENTITY_COLUMNS.
 */
const ENTITY_COLUMNS_BY_LAB: Partial<Record<LabSelection, Partial<Record<EntityType, ColumnDef[]>>>> = {
  2: {
    sample: [
      { key: 'id', label: 'ID' },
      { key: 'sample_name', label: 'Name' },
      { key: 'sample_description', label: 'Description' },
      { key: 'collection_site', label: 'Collection Site' },
      { key: 'collection_datestamp', label: 'Collected' },
      { key: 'complete', label: 'Complete' },
      { key: 'test_count', label: 'Tests' },
    ],
  },
};

export const COMPACT_ENTITY_COLUMNS: Partial<Record<EntityType, ColumnDef[]>> = {
  test: [
    { key: 'id', label: 'ID' },
    { key: 'assay_id.title', label: 'Assay' },
    { key: 'state', label: 'State', render: 'state' },
  ],
  order: [
    { key: 'id', label: 'ID' },
    { key: 'submitted_by', label: 'Submitted By' },
    { key: 'state', label: 'State', render: 'state' },
  ],
  sample: [
    { key: 'id', label: 'ID' },
    { key: 'sample_name', label: 'Name' },
    { key: 'sample_point', label: 'Sample Point' },
    { key: 'complete', label: 'Complete' },
  ],
};

export function getEntityColumns(entityType: EntityType, lab?: LabSelection): ColumnDef[] {
  if (lab) {
    const override = ENTITY_COLUMNS_BY_LAB[lab]?.[entityType];
    if (override) return override;
  }
  return ENTITY_COLUMNS[entityType] ?? [];
}

/**
 * Default-visible columns for a view, honoring per-lab overrides (view.columnsByLab).
 * Falls back to the view's global `columns` when the lab has no override. Mirrors
 * getEntityColumns so result views can tailor their default column set per lab
 * (e.g. Copper One's focused ICP columns) without affecting other labs.
 */
export function getViewColumns(view: ViewDefinition, lab?: LabSelection): ColumnDef[] {
  if (lab != null) {
    const override = view.columnsByLab?.[lab];
    if (override) return override;
  }
  return view.columns;
}

/**
 * Union of a view's curated columns (defaults, per-lab overrides, optionals).
 * Used by the filter modal to replace discovered JSON paths with short labels
 * (e.g. `worksheet_data.report_export_mn.value` → `Mn`).
 */
export function getKnownViewColumns(view: ViewDefinition): ColumnDef[] {
  const byKey = new Map<string, ColumnDef>();
  const add = (cols?: ColumnDef[]) => {
    for (const c of cols ?? []) {
      if (!byKey.has(c.key)) byKey.set(c.key, c);
    }
  };
  add(view.columns);
  for (const cols of Object.values(view.columnsByLab ?? {})) add(cols);
  add(view.optionalColumns);
  return Array.from(byKey.values());
}

/** Short label for a discovered JSON extract, if the view already named it. */
export function labelForExtractColumn(
  attrName: string,
  path: string,
  knownColumns: ColumnDef[],
): string | undefined {
  return knownColumns.find(
    c => c.extract?.from === attrName && c.extract?.path === path,
  )?.label;
}

const ASSAY_ID_TO_VIEWER: Record<number, string> = {};
for (const view of Object.values(VIEW_REGISTRY)) {
  if (view.resultViewer) {
    for (const filter of view.filters) {
      if (filter.attribute_name === 'assay_id' && Array.isArray(filter.value)) {
        for (const id of filter.value) {
          if (typeof id === 'number') {
            ASSAY_ID_TO_VIEWER[id] = view.resultViewer;
          }
        }
      }
    }
  }
}

export function getResultViewer(assayId: number): string | undefined {
  return ASSAY_ID_TO_VIEWER[assayId];
}

export const ALL_ANALYTICAL_ASSAY_IDS: number[] = [
  ...VIEW_REGISTRY.nmr.filters[0].value as number[],
  ...VIEW_REGISTRY.icp.filters[0].value as number[],
  ...VIEW_REGISTRY.shimadzu.filters[0].value as number[],
];

const COMBINED_SAMPLE_COLUMNS: ColumnDef[] = [
  { key: 'sample_id', label: 'Sample ID', render: 'link' },
  { key: 'sample_id.sample_name', label: 'Sample Name' },
  { key: 'sample_id.sample_point', label: 'Sample Point' },
  { key: 'sample_id.collection_site', label: 'Site' },
];

const COMBINED_NMR_COLUMNS: ColumnDef[] = [
  { key: 'nmr_li', label: 'NMR Li', extract: { from: '_nmr', path: 'Li_result' }, sortable: false },
  { key: 'nmr_na', label: 'NMR Na', extract: { from: '_nmr', path: 'Na_result' }, sortable: false },
  { key: 'nmr_cl', label: 'NMR Cl', extract: { from: '_nmr', path: 'Cl' }, sortable: false },
  { key: 'nmr_b', label: 'NMR B', extract: { from: '_nmr', path: 'B_result' }, sortable: false },
  { key: 'nmr_br', label: 'NMR Br', extract: { from: '_nmr', path: 'Br_result' }, sortable: false },
];

const COMBINED_ICP_COLUMNS: ColumnDef[] = ICP_ELEMENTS.map(el => ({
  key: `icp_${el.toLowerCase()}`,
  label: `ICP ${el}`,
  extract: { from: '_icp', path: `report_export_${el.toLowerCase()}.value` },
  sortable: false as const,
}));

const COMBINED_SHIMADZU_COLUMNS: ColumnDef[] = ['TOC', 'TC', 'IC', 'POC', 'NPOC', 'TN'].map(param => ({
  key: `shimadzu_${param.toLowerCase()}`,
  label: `Shim ${param}`,
  extract: { from: '_shimadzu', path: `report_export_result_${param.toLowerCase()}_.value` },
  sortable: false as const,
}));

export const COMBINED_ANALYTICAL_COLUMNS: ColumnDef[] = [
  ...COMBINED_SAMPLE_COLUMNS,
  ...COMBINED_NMR_COLUMNS,
  ...COMBINED_ICP_COLUMNS,
  ...COMBINED_SHIMADZU_COLUMNS,
];
